import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../types/constants.types';
import { FaCheckCircle, FaImage } from 'react-icons/fa';
import { Button } from '../Button';
import ModalShell from '../KioskRegistrations/ModalShell';
import { INPUT_CLASS } from '../KioskRegistrations/registrationUi';
import { showErrorToast, showSuccessToast } from '../../lib/ToastUtility';
import { getSupportedCurrencies, uploadImage } from '../../services/KioskConfigService';
import { submitOwnerInitialSetup } from '../../services/OwnerSetupService';
import { getApiErrorMessage } from '../../utils/apiError';
import type { OwnerKioskSetupStatus, OwnerSetupItem } from '../../types/ownerSetup.types';
import type { CurrencyConfig } from '../../types/kioskConfig.types';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_HEADLINE = 120;

interface Props {
  status: OwnerKioskSetupStatus;
  onCompleted: () => void;
}

export default function OwnerSetupDialog({ status, onCompleted }: Props) {
  const navigate = useNavigate();
  const missing = status.ownerItems.filter((item) => !item.satisfied);
  const blockedByAdmin = status.adminItems.filter((item) => !item.satisfied);

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(missing.map((item) => [item.field, item.kind === 'TEXT' ? item.value || '' : ''])),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const currentCurrency = status.currency?.code || 'GBP';
  const [currencyCode, setCurrencyCode] = useState(currentCurrency);
  const [currencies, setCurrencies] = useState<CurrencyConfig[]>([]);

  useEffect(() => {
    let cancelled = false;
    getSupportedCurrencies()
      .then((list) => {
        if (!cancelled) setCurrencies(list);
      })
      .catch(() => undefined); // The channel keeps its current currency if the list cannot be loaded.
    return () => {
      cancelled = true;
    };
  }, []);

  // Always offer the current currency, even if the list is slow or does not include it.
  const currencyOptions = currencies.some((option) => option.code === currentCurrency)
    ? currencies
    : [
        { code: currentCurrency, symbol: status.currency?.symbol || '', name: status.currency?.name || currentCurrency },
        ...currencies,
      ];

  const setValue = (field: string, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const handleFile = async (item: OwnerSetupItem, file: File | undefined) => {
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      setErrors((current) => ({ ...current, [item.field]: 'Please choose a PNG, JPEG, WebP or GIF image.' }));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setErrors((current) => ({ ...current, [item.field]: 'The image must be 5MB or smaller.' }));
      return;
    }

    setUploading((current) => ({ ...current, [item.field]: true }));
    try {
      const url = await uploadImage(file);
      setValue(item.field, url);
      setPreviews((current) => ({ ...current, [item.field]: URL.createObjectURL(file) }));
    } catch (error) {
      setErrors((current) => ({
        ...current,
        [item.field]: getApiErrorMessage(error, 'Unable to upload the image. Please try again.'),
      }));
    } finally {
      setUploading((current) => ({ ...current, [item.field]: false }));
    }
  };

  const validate = (): Record<string, string> => {
    const found: Record<string, string> = {};
    for (const item of missing) {
      const value = (values[item.field] || '').trim();
      if (!value) {
        found[item.field] = item.kind === 'IMAGE' ? 'Please upload an image.' : 'Please enter a headline.';
      } else if (item.kind === 'TEXT' && value.length > MAX_HEADLINE) {
        found[item.field] = `Please keep this to ${MAX_HEADLINE} characters or fewer.`;
      }
    }
    return found;
  };

  const submit = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      requestAnimationFrame(() =>
        document.querySelector<HTMLElement>('[data-setup-error="true"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
      );
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, string> = Object.fromEntries(
        missing.map((item) => [item.field, values[item.field].trim()]),
      );
      if (currencyCode !== currentCurrency) payload.currencyCode = currencyCode;
      await submitOwnerInitialSetup(status.kioskId, payload);
      showSuccessToast('Your channel is set up and ready.');
      onCompleted();
    } catch (error) {
      showErrorToast(getApiErrorMessage(error, 'Unable to save your channel setup. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const anyUploading = Object.values(uploading).some(Boolean);

  return (
    <ModalShell
      title="Finish setting up your channel"
      size="lg"
      onClose={() => undefined}
      dismissible={false}
      footer={
        <>
          {/* Not a way to skip the setup: just so nobody is ever trapped behind the dialog. */}
          <button
            type="button"
            onClick={() => navigate(AppRoutes.LOGOUT)}
            disabled={saving}
            className="mr-auto text-sm text-gray-500 underline-offset-2 hover:underline dark:text-gray-400"
          >
            Sign out
          </button>
          <Button onClick={submit} loading={saving} disabled={anyUploading || saving}>
            Save and finish
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Your channel{status.kioskName ? <strong> {status.kioskName}</strong> : null} needs a few things before it can
        start taking donations, so this step is required. It only takes a minute, and you can fine-tune everything else
        later in Kiosk CMS.
      </p>

      <div className="mt-5 space-y-5">
        <div>
          <label htmlFor="setup-currency" className="block text-sm font-medium text-gray-800 dark:text-gray-100">
            Currency
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            The currency your donors give in. Check it now: later changes go through admin review.
          </p>
          <select
            id="setup-currency"
            value={currencyCode}
            onChange={(event) => setCurrencyCode(event.target.value)}
            disabled={saving}
            className={`${INPUT_CLASS} mt-1.5`}
          >
            {currencyOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {option.code} · {option.name}
                {option.symbol && option.symbol !== option.code ? ` (${option.symbol})` : ''}
              </option>
            ))}
          </select>
        </div>

        {missing.map((item) => {
          const error = errors[item.field];
          return (
            <div key={item.key} data-setup-error={error ? 'true' : undefined}>
              <label
                htmlFor={`setup-${item.field}`}
                className="block text-sm font-medium text-gray-800 dark:text-gray-100"
              >
                {item.label}
              </label>
              {item.hint && <p className="text-xs text-gray-500 dark:text-gray-400">{item.hint}</p>}

              {item.kind === 'TEXT' ? (
                <input
                  id={`setup-${item.field}`}
                  value={values[item.field] || ''}
                  onChange={(event) => setValue(item.field, event.target.value)}
                  maxLength={MAX_HEADLINE + 20}
                  aria-invalid={error ? true : undefined}
                  className={`${INPUT_CLASS} mt-1.5 ${error ? 'border-red-400' : ''}`}
                />
              ) : (
                <div
                  className={`mt-1.5 flex items-center gap-4 rounded-xl border border-dashed p-3 ${
                    error ? 'border-red-400 bg-red-50 dark:bg-red-950/20' : 'border-emerald-300 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/10'
                  }`}
                >
                  <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-slate-800">
                    {previews[item.field] ? (
                      <img src={previews[item.field]} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <FaImage className="text-gray-400" aria-hidden="true" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={Boolean(uploading[item.field])}
                      onClick={() => fileInputs.current[item.field]?.click()}
                    >
                      {values[item.field] ? 'Change image' : 'Choose image'}
                    </Button>
                    {values[item.field] && !uploading[item.field] && (
                      <span className="ml-3 inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
                        <FaCheckCircle aria-hidden="true" /> Uploaded
                      </span>
                    )}
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      PNG, JPEG, WebP or GIF, up to 5MB. Wide, high-resolution pictures look best.
                    </p>
                  </div>
                  <input
                    id={`setup-${item.field}`}
                    ref={(element) => {
                      fileInputs.current[item.field] = element;
                    }}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="sr-only"
                    tabIndex={-1}
                    onChange={(event) => {
                      void handleFile(item, event.target.files?.[0]);
                      event.target.value = '';
                    }}
                  />
                </div>
              )}
              {error && (
                <p role="alert" className="mt-1.5 text-xs text-red-600">
                  {error}
                </p>
              )}
            </div>
          );
        })}

        {blockedByAdmin.length > 0 && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
            The E2G team still needs to set up: {blockedByAdmin.map((item) => item.label.toLowerCase()).join(', ')}.
            They have been notified, so there is nothing for you to do here.
          </div>
        )}
      </div>
    </ModalShell>
  );
}
